import {
  Component,
  OnInit,
  OnDestroy,
  NgZone,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  ViewChild,
  ElementRef
} from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MatSnackBar } from '@angular/material';
import { FontPickerDirective } from 'ngx-font-picker';
import { TranslateService } from '@ngx-translate/core';
import { ReservationThemeService, ThemeTokens, ReservationThemeData } from './services/reservation-theme.service';
import { ClientResolverService } from '@app/core/client-resolver.service';
import {
  COLOR_TOKENS,
  PRESETS,
  DEFAULT_PRIMARY_HEX,
  DEFAULT_PRIMARY_FONT,
  DEFAULT_SECONDARY_FONT,
  DEFAULT_HSL_VALUES,
  WIDGET_SCRIPT_URL,
  WIDGET_ASSET_BASE,
  GROUP_ORDER,
  DEFAULT_EXPANDED,
  TokenGroup,
  TokenRow
} from './reservation-theme-customizer.constants';

@Component({
  selector: 'app-web-theme-customizer',
  templateUrl: './reservation-theme-customizer.component.html',
  styleUrls: ['./reservation-theme-customizer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class WebThemeCustomizerComponent implements OnInit, OnDestroy {
  presetNames = Object.keys(PRESETS);

  private hslValues: ThemeTokens = { ...DEFAULT_HSL_VALUES };

  tokenGroups: TokenGroup[] = this.buildGroups(this.hslValues);

  selectedPreset = 'Standard';
  isSaving = false;

  activePrimaryHex = DEFAULT_PRIMARY_HEX;

  primaryFont = DEFAULT_PRIMARY_FONT;
  secondaryFont = DEFAULT_SECONDARY_FONT;
  primaryFontObj: any = null;
  secondaryFontObj: any = null;

  private parentPrimaryFont = DEFAULT_PRIMARY_FONT;
  private parentSecondaryFont = DEFAULT_SECONDARY_FONT;
  private loadedData: Partial<ReservationThemeData> = {};

  activeClientId = 0;

  readonly widgetScriptUrl = WIDGET_SCRIPT_URL;
  readonly widgetAssetBase = WIDGET_ASSET_BASE;

  @ViewChild('secondaryFontPickerRef', { static: false }) secondaryFontPickerRef: FontPickerDirective;
  @ViewChild('widgetPreview', { static: false }) widgetPreviewRef: ElementRef<HTMLElement>;

  private get previewEl(): HTMLElement | null {
    return this.widgetPreviewRef ? this.widgetPreviewRef.nativeElement : null;
  }

  private ngUnsubscribe = new Subject<any>();

  constructor(
    private webThemeService: ReservationThemeService,
    private clientResolver: ClientResolverService,
    private snackBar: MatSnackBar,
    private zone: NgZone,
    private cdr: ChangeDetectorRef,
    private translate: TranslateService
  ) {
    this.activeClientId = this.clientResolver.client ? this.clientResolver.client.id : 0;
  }

  ngOnInit() {
    this.loadTheme();
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  trackByGroup(_i: number, g: TokenGroup): string {
    return g.name;
  }
  trackByKey(_i: number, r: TokenRow): string {
    return r.key;
  }

  toggleGroup(group: TokenGroup) {
    group.expanded = !group.expanded;
    this.cdr.markForCheck();
  }

  onColorChange(row: TokenRow, hex: string) {
    const hsl = this.hexToHsl(hex);
    this.hslValues = { ...this.hslValues, [row.key]: hsl };
    row.hex = hex;
    this.selectedPreset = '';
    this.zone.runOutsideAngular(() => {
      this.previewEl.style.setProperty(row.key, hsl, 'important');
    });
    this.syncDerivedState();
    this.cdr.markForCheck();
  }

  applyPreset(name: string) {
    const merged: ThemeTokens = {
      ...DEFAULT_HSL_VALUES,
      ...(PRESETS[name] || {}),
      '--font-primary': this.primaryFont,
      '--font-secondary': this.secondaryFont
    };
    this.zone.runOutsideAngular(() => {
      COLOR_TOKENS.forEach(t => this.previewEl.style.removeProperty(t.key));
    });
    this.applyAllToDOM(merged);
    this.hslValues = merged;
    this.selectedPreset = name;
    this.tokenGroups = this.buildGroups(this.hslValues, this.tokenGroups);
    this.syncDerivedState();
    this.cdr.markForCheck();
  }

  changePrimaryFont(e: any) {
    this.primaryFont = e.family;
    this.hslValues = { ...this.hslValues, '--font-primary': e.family };
    this.zone.runOutsideAngular(() => {
      this.previewEl.style.setProperty('--font-primary', e.family, 'important');
    });
    this.cdr.markForCheck();
  }

  changeSecondaryFont(e: any) {
    this.secondaryFont = e.family;
    this.hslValues = { ...this.hslValues, '--font-secondary': e.family };
    this.zone.runOutsideAngular(() => {
      this.previewEl.style.setProperty('--font-secondary', e.family, 'important');
    });
    this.cdr.markForCheck();
    if (this.secondaryFontPickerRef) {
      this.secondaryFontPickerRef.closeDialog();
    }
  }

  save() {
    this.isSaving = true;
    this.cdr.markForCheck();

    this.webThemeService
      .saveWidgetTheme({
        ...this.loadedData,
        themeTokens: this.hslValues,
        presetName: this.selectedPreset,
        primaryFont: this.parentPrimaryFont,
        secondaryFont: this.parentSecondaryFont
      })
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(
        () => {
          this.isSaving = false;
          this.snackBar.open(this.translate.instant('reservationTheme.messages.saveSuccess'), '', {
            duration: 2000,
            panelClass: ['snackbar-success']
          });
          this.cdr.markForCheck();
        },
        () => {
          this.isSaving = false;
          this.snackBar.open(this.translate.instant('reservationTheme.messages.saveError'), '', {
            duration: 3000,
            panelClass: ['snackbar-error']
          });
          this.cdr.markForCheck();
        }
      );
  }

  reset() {
    this.zone.runOutsideAngular(() => {
      COLOR_TOKENS.forEach(t => this.previewEl.style.removeProperty(t.key));
      this.previewEl.style.removeProperty('--font-primary');
      this.previewEl.style.removeProperty('--font-secondary');
    });
    this.hslValues = { ...DEFAULT_HSL_VALUES };
    this.applyAllToDOM(this.hslValues);
    this.selectedPreset = 'Standard';
    this.primaryFont = DEFAULT_PRIMARY_FONT;
    this.secondaryFont = DEFAULT_SECONDARY_FONT;
    this.primaryFontObj = null;
    this.secondaryFontObj = null;
    this.tokenGroups = this.buildGroups(this.hslValues, this.tokenGroups);
    this.syncDerivedState();
    this.snackBar.open(this.translate.instant('reservationTheme.messages.resetSuccess'), '', { duration: 2000 });
    this.cdr.markForCheck();
  }

  private syncDerivedState() {
    this.activePrimaryHex = this.hslValues['--primary']
      ? this.hslToHex(this.hslValues['--primary'])
      : DEFAULT_PRIMARY_HEX;
  }

  private loadTheme() {
    this.webThemeService
      .getWidgetTheme()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(data => {
        this.loadedData = data || {};
        const saved = data.themeTokens || {};
        this.hslValues = {
          ...DEFAULT_HSL_VALUES,
          ...saved,
          '--font-primary': saved['--font-primary'] || DEFAULT_PRIMARY_FONT,
          '--font-secondary': saved['--font-secondary'] || DEFAULT_SECONDARY_FONT
        };
        this.primaryFont = this.hslValues['--font-primary'];
        this.secondaryFont = this.hslValues['--font-secondary'];
        this.parentPrimaryFont = data.primaryFont || DEFAULT_PRIMARY_FONT;
        this.parentSecondaryFont = data.secondaryFont || DEFAULT_SECONDARY_FONT;
        this.selectedPreset = data.presetName || 'Standard';
        this.applyAllToDOM(this.hslValues);
        this.tokenGroups = this.buildGroups(this.hslValues, this.tokenGroups);
        this.syncDerivedState();
        this.cdr.markForCheck();
      });
  }

  private buildGroups(hsl: ThemeTokens, existing?: TokenGroup[]): TokenGroup[] {
    const expandedMap: Record<string, boolean> = {};
    if (existing) {
      existing.forEach(g => {
        expandedMap[g.name] = g.expanded;
      });
    }
    return GROUP_ORDER.map(name => ({
      name,
      expanded: name in expandedMap ? expandedMap[name] : DEFAULT_EXPANDED.has(name),
      rows: COLOR_TOKENS.filter(t => t.group === name).map(t => ({
        ...t,
        hex: hsl[t.key] ? this.hslToHex(hsl[t.key]) : '#000000'
      }))
    }));
  }

  private applyAllToDOM(tokens: ThemeTokens) {
    this.zone.runOutsideAngular(() => {
      const el = this.previewEl;
      if (!el) {
        return;
      }
      Object.entries(tokens).forEach(([key, val]) => {
        if (val) {
          el.style.setProperty(key, val, 'important');
        }
      });
    });
  }

  private hslToHex(hsl: string): string {
    const parts = hsl.trim().split(/\s+/);
    if (parts.length < 3) {
      return '#000000';
    }
    const h = parseFloat(parts[0]);
    const s = parseFloat(parts[1]) / 100;
    const l = parseFloat(parts[2]) / 100;
    const a = s * Math.min(l, 1 - l);
    const f = (n: number) => {
      const k = (n + h / 30) % 12;
      const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
      return Math.round(255 * color)
        .toString(16)
        .padStart(2, '0');
    };
    return `#${f(0)}${f(8)}${f(4)}`;
  }

  private hexToHsl(hex: string): string {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (!result) {
      return '0 0% 0%';
    }
    const r = parseInt(result[1], 16) / 255;
    const g = parseInt(result[2], 16) / 255;
    const b = parseInt(result[3], 16) / 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h = 0;
    let s = 0;
    const l = (max + min) / 2;
    if (max !== min) {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r:
          h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
          break;
        case g:
          h = ((b - r) / d + 2) / 6;
          break;
        case b:
          h = ((r - g) / d + 4) / 6;
          break;
      }
    }
    return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
  }
}
